import { useCelebrityStore } from "@/state/celebrity-store";
import { useCelebrities } from "../hooks/use-celebrities";
import { DIFFICULTY_LABELS } from "@/features/celebrity/types";
import type { Celebrity, CelebrityDifficulty } from "@/features/celebrity/types";
import { Button } from "@pushup/ui";
import { cn } from "@pushup/ui";
import { User, Swords, X, Loader2 } from "lucide-react";

const DIFFICULTY_COLORS: Record<CelebrityDifficulty, string> = {
  easy: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  medium: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  hard: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  beast: "bg-red-500/20 text-red-300 border-red-500/30",
};

interface CelebrityPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Modal overlay shown before starting a workout.
 * Lets the user pick a celebrity opponent or go solo.
 */
export function CelebrityPicker({ open, onClose, onConfirm }: CelebrityPickerProps) {
  const selectedId = useCelebrityStore((s) => s.selectedCelebrityId);
  const selectCelebrity = useCelebrityStore((s) => s.selectCelebrity);
  const { data: celebrities, isLoading, isError } = useCelebrities();

  if (!open) return null;

  const roster = celebrities ?? [];

  const handleSelect = (id: string | null) => {
    selectCelebrity(id);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-900 p-6 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Close"
        >
          <X className="size-5" />
        </button>

        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Swords className="size-6 text-orange-400" />
          <div>
            <h2 className="text-lg font-bold text-white">Choose Your Opponent</h2>
            <p className="text-sm text-white/60">
              Pick a celebrity to compete against, or go solo.
            </p>
          </div>
        </div>

        {/* Solo option */}
        <button
          onClick={() => handleSelect(null)}
          className={cn(
            "mb-3 flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all",
            selectedId === null
              ? "border-orange-500/50 bg-orange-500/10"
              : "border-white/10 bg-white/5 hover:bg-white/10",
          )}
        >
          <div className="flex size-12 items-center justify-center rounded-full bg-neutral-700">
            <User className="size-6 text-white/70" />
          </div>
          <div>
            <div className="font-semibold text-white">Solo Mode</div>
            <div className="text-xs text-white/50">Track your own reps without an opponent</div>
          </div>
          {selectedId === null && (
            <div className="ml-auto rounded-full bg-orange-500 px-2.5 py-0.5 text-xs font-semibold text-white">
              Selected
            </div>
          )}
        </button>

        {/* Celebrity grid */}
        {roster.length > 0 && (
          <div className="max-h-[40vh] space-y-2 overflow-y-auto pr-1">
            {roster.map((celeb) => (
              <CelebrityCard
                key={celeb.id}
                celebrity={celeb}
                selected={selectedId === celeb.id}
                onSelect={() => handleSelect(celeb.id)}
              />
            ))}
          </div>
        )}

        {roster.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/20 p-6 text-center">
            {isLoading ? (
              <p className="flex items-center justify-center gap-2 text-sm text-white/50">
                <Loader2 className="size-4 animate-spin" />
                Loading opponents…
              </p>
            ) : isError ? (
              <p className="text-sm text-white/50">
                Couldn&apos;t load opponents. Check your connection and try again.
              </p>
            ) : (
              <p className="text-sm text-white/50">No opponents available yet.</p>
            )}
          </div>
        )}

        {/* Confirm button */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-xs text-white/50">Tap the screen to start when you&apos;re ready.</p>
          <Button onClick={onConfirm} size="lg" className="gap-2 px-8 font-semibold shadow-lg">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function CelebrityCard({
  celebrity,
  selected,
  onSelect,
}: {
  celebrity: Celebrity;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all",
        selected
          ? "border-pink-500/50 bg-pink-500/10"
          : "border-white/10 bg-white/5 hover:bg-white/10",
      )}
    >
      {/* Avatar */}
      <div className="relative size-12 shrink-0">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 p-[2px]">
          <div className="flex size-full items-center justify-center overflow-hidden rounded-full bg-neutral-800">
            {celebrity.imageDataUrl ? (
              <img
                src={celebrity.imageDataUrl}
                alt={celebrity.name}
                className="size-full object-cover"
              />
            ) : (
              <span className="text-sm font-bold text-white/60">
                {celebrity.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white truncate">{celebrity.name}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 text-[10px] font-medium",
              DIFFICULTY_COLORS[celebrity.difficulty],
            )}
          >
            {DIFFICULTY_LABELS[celebrity.difficulty]}
          </span>
          <span className="text-[10px] text-white/40">{celebrity.secondsPerRep}s/rep</span>
          {celebrity.voicePhases.length > 0 && (
            <span className="text-[10px] text-white/40">
              🎤 {celebrity.voicePhases.length} clips
            </span>
          )}
        </div>
      </div>

      {/* Selected indicator */}
      {selected && (
        <div className="ml-auto shrink-0 rounded-full bg-pink-500 px-2.5 py-0.5 text-xs font-semibold text-white">
          Selected
        </div>
      )}
    </button>
  );
}
